import React, { useMemo, useState } from 'react';
import { Button, Col, Flex, Popover, Row, Select, Space } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { useQuery } from 'react-query';
import { Moment } from 'moment';
import { formatCurrency } from '@shared/utils/currency';
import { formatDate } from '@shared/utils/date';
import { getReceivableLabel, longReceivableRanges, shortReceivableRanges } from '@shared/utils/receivablesHelpers';
import { CsvDelimiter, DashboardPeriods, DashboardTypes, DaysDashboardPeriods, NumberFormatStyle } from '@shared/utils/constants';
import type { OutStandingData } from '@api/features/statistics/getOutstandingsData';
import { useAuth } from '@client/lib/auth';
import { getReceivablesAgingData } from '@client/lib/api';
import Table from '@client/components/Table/Table';
import useNumberFormatStyle from '@client/utils/hooks/useExportNotation';
import { useDashboardCurrency, useDashboardPeriod, DashboardComponent } from '../helpers/dashboardInfo';
import styles from './ReceivablesAgingDashboard.module.css';
import OutstandingsTableSection from './OutstandingsTableSection';

type DashboardRow = {
	counterparty: string | null;
	notDueReceivable: number;
	overflowReceivable: number;
	total: number;
	receivable: {
		range: [Moment, Moment];
		label: string;
		receivable: number;
	}[];
};

type DashboardTotalRow = {
	isTotal: true;
	counterparty: React.ReactNode;
	totals: number[];
	notDueReceivable: number;
	overflowReceivable: number;
	total: number;
};

type TableRow = DashboardRow | DashboardTotalRow;

const isTotalRow = (row: TableRow): row is DashboardTotalRow => (row as DashboardTotalRow).isTotal === true;

const formatCsvNumber = (value: number, formatStyle: NumberFormatStyle) => {
	if (!Number.isFinite(value)) {
		return '';
	}

	const v =
		formatStyle === NumberFormatStyle.UkUs ?
			value.toFixed(2) :
			value.toFixed(2).replace('.', ',');

	return String(v);
};

const ReceivablesAgingDashboard: DashboardComponent = ({ setLastUpdated }) => {
	const { userInfo } = useAuth();
	const { formatStyle, setFormatStyle } = useNumberFormatStyle(NumberFormatStyle.UkUs);
	const [tableRows, setTableRows] = useState<OutStandingData[]>([]);

	const period = useDashboardPeriod();
	const currencyCtx = useDashboardCurrency();
	const currency = currencyCtx?.currency;
	const daysPeriod = (period ?? DashboardPeriods.SIXTY_DAYS) as DaysDashboardPeriods;

	const { data, isFetching: loading } = useQuery(
		[DashboardTypes.AGED_RECEIVABLES, period],
		() => getReceivablesAgingData(daysPeriod),
		{
			onSuccess: (d) => {
				setLastUpdated(d.lastUpdated);

				if (!currencyCtx) {
					return;
				}

				const availableCurrencies = d.availableCurrencies ?? [];
				currencyCtx.setAvailableCurrencies(availableCurrencies);

				const current = currencyCtx.currency;

				if (current && availableCurrencies.includes(current)) {
					return;
				}

				const next = d.currency ?? availableCurrencies[0];

				if (next) {
					currencyCtx.setCurrency(next);
				}
			},
			refetchOnWindowFocus: false,
		},
	);

	const selectedCurrency = currency ?? data?.currency ?? userInfo.baseCurrency;
	const dashboardData = data?.dataByCurrency?.[selectedCurrency] ?? [];

	const ranges = daysPeriod === DashboardPeriods.SIXTY_DAYS ? shortReceivableRanges : longReceivableRanges;

	const tableData = useMemo<TableRow[]>(() => {
		const totalsByRange = ranges.map((_r, i) =>
			dashboardData.reduce((t, cp) => t + (cp.receivable[i]?.receivable ?? 0), 0),
		);

		const totalNotDue = dashboardData.reduce((t, cp) => t + cp.notDueReceivable, 0);
		const totalOverflow = dashboardData.reduce((t, cp) => t + cp.overflowReceivable, 0);
		const grandTotal = dashboardData.reduce((t, cp) => t + cp.total, 0);

		const totalRow: DashboardTotalRow = {
			isTotal: true,
			counterparty: <b>Total</b>,
			totals: totalsByRange,
			notDueReceivable: totalNotDue,
			overflowReceivable: totalOverflow,
			total: grandTotal,
		};

		return [...dashboardData, totalRow];
	}, [dashboardData, ranges]);

	const agedCsvHeaders = useMemo(() => ([
		'Counterparty',
		'Not Due',
		...ranges.map((_r, i) => getReceivableLabel(i, daysPeriod)),
		daysPeriod === DashboardPeriods.SIXTY_DAYS ? '+60 days' : '+120 days',
		'Total',
	]), [ranges, daysPeriod]);

	const agedCsvRows = useMemo(() => {
		const base = dashboardData.map((cp) => ([
			cp.counterparty ?? '',
			formatCsvNumber(cp.notDueReceivable ?? 0, formatStyle),
			...ranges.map((_r, i) => formatCsvNumber(cp.receivable?.[i]?.receivable ?? 0, formatStyle)),
			formatCsvNumber(cp.overflowReceivable ?? 0, formatStyle),
			formatCsvNumber(cp.total ?? 0, formatStyle),
		]));

		const totalNotDue = dashboardData.reduce((t, cp) => t + (cp.notDueReceivable ?? 0), 0);
		const totalOverflow = dashboardData.reduce((t, cp) => t + (cp.overflowReceivable ?? 0), 0);
		const totalsByRange = ranges.map((_r, i) =>
			dashboardData.reduce((t, cp) => t + (cp.receivable?.[i]?.receivable ?? 0), 0),
		);
		const grandTotal = dashboardData.reduce((t, cp) => t + (cp.total ?? 0), 0);

		const totalRow = ([
			'Total',
			formatCsvNumber(totalNotDue, formatStyle),
			...totalsByRange.map((v) => formatCsvNumber(v, formatStyle)),
			formatCsvNumber(totalOverflow, formatStyle),
			formatCsvNumber(grandTotal, formatStyle),
		]);

		return [...base, totalRow];
	}, [dashboardData, ranges, formatStyle]);

	const outstandingsCsvHeaders = [
		'Vessel',
		'Contract',
		'Contract type',
		'Counterparty',
		'Description',
		'Invoice #',
		'Invoice date',
		'Due date',
		'Days outstanding',
		'Invoiced',
		'Paid',
		'Outstanding',
		'Currency',
	];

	const outstandingsCsvRows = useMemo(() => {
		return tableRows.map((r) => ([
			r.vessel?.name ?? '',
			r.contractIdentifier ?? '',
			r.contractType ? String(r.contractType) : '',
			r.counterparty?.name ?? '',
			r.description ?? '',
			r.invoiceNumber ?? '',
			r.invoiceDate ? formatDate(r.invoiceDate) : '',
			r.dueDate ? formatDate(r.dueDate) : '',
			r.daysOutstanding ?? '',
			formatCsvNumber(r.amount ?? 0, formatStyle),
			formatCsvNumber(r.paid ?? 0, formatStyle),
			formatCsvNumber(r.outstanding ?? 0, formatStyle),
			r.currency ?? '',
		]));
	}, [tableRows, formatStyle]);

	const csvData = useMemo(() => ([
		['Aged Receivables'],
		agedCsvHeaders,
		...agedCsvRows,

		[],
		[],

		['Outstandings (filtered + sorted)'],
		outstandingsCsvHeaders,
		...outstandingsCsvRows,
	]), [agedCsvHeaders, agedCsvRows, outstandingsCsvHeaders, outstandingsCsvRows]);

	return (
		<>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Flex justify="flex-end" style={{ marginBottom: 12 }}>
						<Popover
							trigger="click"
							content={(
								<Space direction="vertical">
									<Select
										className={styles.widerSelect}
										value={formatStyle}
										onChange={(v) => setFormatStyle(v)}
										options={[
											{ label: 'UK/US notation', value: NumberFormatStyle.UkUs },
											{ label: 'European notation', value: NumberFormatStyle.European },
										]}
									/>
									<CSVLink
										data={csvData}
										separator={formatStyle === NumberFormatStyle.UkUs ?
											CsvDelimiter.Comma :
											CsvDelimiter.Semicolon
										}
										enclosingCharacter=""
										filename={`Aged Receivables & Outstandings ${new Date().toISOString().slice(0, 10)}.csv`}
									>
										<Button type="primary" icon={<ExportOutlined />}>
											Export
										</Button>
									</CSVLink>
								</Space>
							)}
						>
							<Button icon={<ExportOutlined />}>Export all to CSV</Button>
						</Popover>
					</Flex>

					<Table<TableRow>
						pagination={false}
						dataSource={tableData}
						loading={loading}
						columns={[
							{
								dataIndex: 'counterparty',
								title: 'Counterparty',
							},
							{
								title: 'Not Due',
								dataIndex: 'notDueReceivable',
								render: (_value: number, row: TableRow) => {
									const formatted = formatCurrency(row.notDueReceivable, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							},
							...ranges.map((_r, i) => ({
								title: getReceivableLabel(i, daysPeriod),
								render: (_value: number | undefined, row: TableRow) => {
									const value = isTotalRow(row) ? row.totals[i] : (row.receivable[i]?.receivable ?? 0);
									const formatted = formatCurrency(value, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							})),
							{
								title: daysPeriod === DashboardPeriods.SIXTY_DAYS ? '+60 days' : '+120 days',
								dataIndex: 'overflowReceivable',
								render: (_value: number, row: TableRow) => {
									const formatted = formatCurrency(row.overflowReceivable, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							},
							{
								title: 'Total',
								dataIndex: 'total',
								render: (total: number, row: TableRow) => {
									const formatted = formatCurrency(total, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							},
						]}
					/>
				</Col>
			</Row>

			<Row gutter={[16, 16]} style={{ marginTop: 24 }}>
				<Col span={24}>
					<OutstandingsTableSection
						title="Outstandings"
						className={styles.table}
						setLastUpdated={(m) => setLastUpdated?.(m)}
						onFilteredRowsChange={setTableRows}
					/>
				</Col>
			</Row>
		</>
	);
};

export default ReceivablesAgingDashboard;